// Utility functions for color manipulation and contrast checking

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code (e.g., "#FF0000" or "FF0000")
 * @returns {object} RGB values {r, g, b}
 */
export const hexToRgb = (hex) => {
  // Remove # if present
  const cleanHex = hex.replace("#", "");

  // Handle 3-digit hex codes
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((char) => char + char)
          .join("")
      : cleanHex;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return { r, g, b };
};

/**
 * Calculate relative luminance of a color
 * @param {object} rgb - RGB values {r, g, b}
 * @returns {number} Relative luminance (0-1)
 */
export const getLuminance = (rgb) => {
  const { r, g, b } = rgb;

  // Convert to sRGB
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  // Apply gamma correction
  const rLinear =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  // Calculate luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio (1-21)
 */
export const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const lum1 = getLuminance(rgb1);
  const lum2 = getLuminance(rgb2);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Get the best text color (black or white) for a given background color
 * @param {string} backgroundColor - Background color (hex)
 * @param {string} lightColor - Light text color (default: "#FFFFFF")
 * @param {string} darkColor - Dark text color (default: "#000000")
 * @returns {string} Best text color (hex)
 */
export const getContrastColor = (
  backgroundColor,
  lightColor = "#FFFFFF",
  darkColor = "#000000"
) => {
  if (!backgroundColor) return darkColor;

  const whiteContrast = getContrastRatio(backgroundColor, lightColor);
  const blackContrast = getContrastRatio(backgroundColor, darkColor);

  // Return the color with better contrast ratio
  return whiteContrast > blackContrast ? lightColor : darkColor;
};

/**
 * Check if contrast ratio meets WCAG standards
 * @param {number} contrastRatio - Contrast ratio
 * @param {string} level - WCAG level ("AA" or "AAA")
 * @param {string} size - Text size ("normal" or "large")
 * @returns {boolean} Whether contrast meets standards
 */
export const meetsWCAGStandards = (
  contrastRatio,
  level = "AA",
  size = "normal"
) => {
  const requirements = {
    AA: {
      normal: 4.5,
      large: 3,
    },
    AAA: {
      normal: 7,
      large: 4.5,
    },
  };

  return contrastRatio >= requirements[level][size];
};
